import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  LayoutDashboard, 
  Ship, 
  Building2, 
  FileText, 
  LogOut, 
  Menu, 
  X,
  User,
  ChevronDown,
  Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const navItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/vessels", label: "Vessel Search", icon: Ship },
  { path: "/companies", label: "Company Search", icon: Building2 },
  { path: "/reports", label: "Report History", icon: FileText },
];

const adminNavItems = [
  { path: "/admin", label: "Admin Panel", icon: Shield },
];

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-[#f8fafc]">
      {/* Sidebar - Desktop */}
      <aside className="hidden md:flex w-64 bg-[#0b3e65] text-white flex-col">
        {/* Logo */}
        <div className="p-4 border-b border-[#1e5a8a]">
          <Link to="/" className="block">
            <img 
              src="https://customer-assets.emergentagent.com/job_fae3f46c-8b67-432c-9c77-692e590edf8a/artifacts/1nzgjyki_GREEN%20BUNKERS%20negarivo.png" 
              alt="Green Bunkers" 
              className="h-14 w-auto object-contain"
            />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                className={`flex items-center gap-3 px-6 py-3 text-sm transition-all ${
                  isActive
                    ? "bg-[#f37022]/20 border-l-3 border-[#f37022] text-white"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                }`}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
          
          {/* Admin nav - only visible for admin users */}
          {user?.role === "admin" && (
            <>
              <div className="mx-6 my-3 border-t border-[#1e5a8a]" />
              {adminNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    data-testid={`nav-admin`}
                    className={`flex items-center gap-3 px-6 py-3 text-sm transition-all ${
                      isActive
                        ? "bg-[#f37022]/20 border-l-3 border-[#f37022] text-white"
                        : "text-slate-300 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-[#1e5a8a]">
          <div className="flex items-center gap-3 text-sm">
            <div className="w-8 h-8 rounded-full bg-[#f37022] flex items-center justify-center">
              <User className="w-4 h-4" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="truncate font-medium">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full mt-3 text-slate-300 hover:text-white hover:bg-white/10"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-6 justify-between shadow-sm">
          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 -ml-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>

          {/* Page Title */}
          <h1 className="font-serif text-xl text-[#0b3e65] hidden md:block">
            {[...navItems, ...adminNavItems].find(item => item.path === location.pathname)?.label || "Dashboard"}
          </h1>

          {/* Mobile Logo */}
          <div className="md:hidden">
            <img 
              src="https://customer-assets.emergentagent.com/job_compliance-vessel/artifacts/1ghcrk1z_GREEN%20BUNKERS%20Horizontal.png" 
              alt="Green Bunkers" 
              className="h-7 w-auto"
            />
          </div>

          {/* User Dropdown - Desktop */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="hidden md:flex items-center gap-2" data-testid="user-menu-btn">
                <div className="w-8 h-8 rounded-full bg-[#0b3e65] text-white flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
                <span className="text-sm">{user?.name}</span>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout} data-testid="dropdown-logout">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-white border-b border-slate-200 shadow-lg z-50">
            <nav className="py-2">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-6 py-3 text-sm ${
                      isActive
                        ? "bg-[#0b3e65]/5 text-[#0b3e65] font-medium"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
              {user?.role === "admin" && adminNavItems.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-6 py-3 text-sm ${
                      isActive
                        ? "bg-[#0b3e65]/5 text-[#0b3e65] font-medium"
                        : "text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </Link>
                );
              })}
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-6 py-3 text-sm text-red-600 hover:bg-red-50 w-full"
              >
                <LogOut className="w-5 h-5" />
                Logout
              </button>
            </nav>
          </div>
        )}

        {/* Page Content */}
        <main className="flex-1 overflow-auto p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
